import { useRef, useState } from 'react';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@repo/ui/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@repo/ui/components/ui/dialog';
import type { Photographer } from '@/services/photovogue';

export const PhotographerActions = ({
  photographer,
  disabled,
  onDelete,
}: {
  photographer: Photographer;
  disabled: boolean;
  onDelete: (id: string) => Promise<boolean>;
}) => {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef(false);

  const confirmDelete = async () => {
    if (pending.current) return;
    pending.current = true;
    setDeleting(true);
    setError(false);
    try {
      if (await onDelete(photographer.id)) setOpen(false);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      pending.current = false;
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={`Actions for ${photographer.name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onCloseAutoFocus={(event) => {
            if (open) event.preventDefault();
          }}
        >
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              setError(false);
              setOpen(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete photographer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!pending.current) setOpen(next);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {photographer.name}?</DialogTitle>
            <DialogDescription>
              This permanently removes photographer ID {photographer.id}, their
              PhotoVogue profile, photos, related records, and image files in
              the new PhotoVogue storage. Their account profile (UPS) and legacy
              image copies are retained. Cached images may remain accessible.
              They can enroll again, but deleted photos will not be restored.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              Deletion could not be confirmed. Some image files may already have
              been removed. Refresh the list before retrying. If uploads are
              active, wait for them to finish or expire.
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              autoFocus
              disabled={deleting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={disabled || deleting}
              onClick={confirmDelete}
            >
              {deleting ? 'Deleting…' : 'Delete photographer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
